import {
  Route,
  Routes,
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { entities, ComposeProviders } from "byzantine";
import { Features } from "byzantine/src/Feature";
import {
  BulkPaymentFormType,
  getBulkPaymentStartDate,
  initialValues,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import {
  canShowIndividualPaymentActivity,
  canShowPaymentActivity,
  canStartAchFileImport,
  canUseAchPassthrough,
  canViewAchFileImports,
} from "byzantine/src/dbbl/businessLogic/modules/features/paymentActivity";
import {
  InstitutionSettingsContextProvider,
  useInstitutionSettings,
} from "../contexts/InstitutionSettingsContext";
import NachaPassthroughPaymentsPage from "../DualApproval/PassthroughPayments/NachaPassthroughPaymentsPage";
import { areAchTemplatesAvailable } from "../Templates/constants";
import { SudoProvider, SudoRoutes } from "../SudoContext";
import RequestOTP from "../SudoContext/RequestOTP";
import SubmitOTP from "../SudoContext/SubmitOTP";
import PaymentActivityContainer from "./PaymentActivityContainer";
import { IndividualPaymentContainer } from "./Individual";
import { BulkPaymentContainer, BulkSelectReversalScreen } from "./Bulk";
import { FileImportContainer } from "./FileImport";
import {
  AdditionalDetailsScreen,
  PaymentDetailsScreen,
  ReviewBulkScreen,
  ReceiptBulkScreen,
  PaymentsTableScreen,
  FileUploadScreen,
  ColumnMappingScreen,
  RecipientReviewScreen,
  ValidationResultsScreen,
  PassthroughAdditionalDetailsScreen,
  PassthroughReviewScreen,
  PassthroughReceiptScreen,
} from "./CreateBulk";
import SelectTemplateScreen from "./CreateBulk/templates/SelectTemplateScreen";
import { TemplateContextProvider } from "./CreateBulk/templates/TemplateContext";
import { ACHImportContextProvider } from "./ACHImportContext";
import { ACHPassthroughImportContextProvider } from "./ACHPassthroughImportContext";
import { DomesticWireImportContextProvider } from "./WireFileImport";
import WireColumnMappingScreen from "./WireFileImport/WireColumnMappingScreen";
import WireRecipientReviewScreen from "./WireFileImport/WireRecipientReviewScreen";
import WirePaymentDetailsScreen from "./WireFileImport/WirePaymentDetailsScreen";
import WirePaymentsTableScreen from "./WireFileImport/WirePaymentsTableScreen";
import WireReviewScreen from "./WireFileImport/WireReviewScreen";
import WireReceiptScreen from "./WireFileImport/WireReceiptScreen";

type ImportContextProvider =
  | typeof DomesticWireImportContextProvider
  | typeof ACHPassthroughImportContextProvider;

const Providers = [
  entities.paymentActivity.bootstrap.Provider,
  entities.bulkPayments.bootstrap.Provider,
  entities.bulkTemplates.bootstrap.Provider,
  entities.fileImportPayments.bootstrap.Provider,
];
export const PAYMENT_ACTIVITY_ROUTE = "payment_activity";

/*
 * reverse flow has 2 flows (drawer vs table entry) with slightly different URL structures
 * the only way to keep the drawer open for one entry point but not the other is to make the
 * two URLs differ in whether they bind `:activityUuid`:
 *   - drawer footer  → /payment_activity/{uuid}/reverse   (binds :activityUuid → drawer
 *                       stays open behind the modal, including through the sudo step)
 *   - table dropdown → /payment_activity/reverse/{uuid}   ("reverse" first, so {uuid}
 *                       never binds :activityUuid → no drawer behind the modal)
 */
export const getReverseRoute = <T extends string>(activityUuid: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/${activityUuid}/reverse` as const;

export const getReverseFromListRoute = <T extends string>(activityUuid: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/reverse/${activityUuid}` as const;

export const getBulkReversalSelectionRoute = <T extends string>(
  bulkPaymentId: T
) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/bulk/${bulkPaymentId}/select-reversals` as const;

// the bulk reverse modal flow. reached three ways:
//   - "reverse all eligible" (bulk table) → getBulkReverseRoute
//   - "reverse all eligible" (bulk drawer) → getBulkReverseFromDrawerRoute
//   - "reverse selected" (selection screen) → nested under the selection route so
//     its table stays mounted behind the modal
export const getBulkReverseRoute = <T extends string>(bulkPaymentId: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/bulk/reverse/${bulkPaymentId}` as const;

export const getBulkReverseFromDrawerRoute = <T extends string>(
  bulkPaymentId: T
) => `/${PAYMENT_ACTIVITY_ROUTE}/bulk/${bulkPaymentId}/reverse` as const;

export const getBulkReverseFromSelectionRoute = <T extends string>(
  bulkPaymentId: T
) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/bulk/${bulkPaymentId}/select-reversals/reverse` as const;

// file import reversal routes. same as bulk but scoped to file-import
export const getFileImportReversalSelectionRoute = <T extends string>(id: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${id}/select-reversals` as const;

export const getFileImportReverseRoute = <T extends string>(id: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/file-import/reverse/${id}` as const;

export const getFileImportReverseFromDrawerRoute = <T extends string>(id: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${id}/reverse` as const;

export const getFileImportReverseFromSelectionRoute = <T extends string>(
  id: T
) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${id}/select-reversals/reverse` as const;

export const getNachaPassthroughPaymentsRoute = <T extends string>(id: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${id}/payments` as const;

export const getFileImportDrawerRoute = <T extends string>(id: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${id}` as const;

export const getNachaPassthroughDrawerRoute = <T extends string>(id: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/file-import/ach-passthrough/${id}` as const;

export const getWireImportDrawerRoute = <T extends string>(id: T) =>
  `/${PAYMENT_ACTIVITY_ROUTE}/file-import/wire/${id}` as const;

const initialBulkFormValues = (
  institution: API.Institution
): BulkPaymentFormType => {
  const scheduledDate = getBulkPaymentStartDate(
    institution.features?.expedited_payments,
    institution.ach_next_cutoff,
    institution.features?.fednow_send
  );

  // we only default set 1 payment row in BulkPaymentForm
  return {
    ...initialValues,
    payments: [
      {
        ...initialValues.payments[0],
        scheduledDate,
      },
    ],
  };
};

const RequireBulkTitle = ({ children }: { children: React.ReactNode }) => {
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const { values } = form;
  const { search } = useLocation();

  if (!values.title) {
    return (
      <Navigate
        to={{ pathname: `/${PAYMENT_ACTIVITY_ROUTE}/bulk/new`, search }}
        replace
      />
    );
  }

  return <>{children}</>;
};

// Catch-all redirect for unknown paths under bulk/new.
// Preserves search params so ?templateId isn't lost.
const BulkNewCatchAll = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  return (
    <Navigate
      to={`/${PAYMENT_ACTIVITY_ROUTE}/bulk/new${search ? `?${search}` : ""}`}
      replace
    />
  );
};

const PaymentActivityRoutes = ({
  features,
  limitsJson,
}: {
  features: Features;
  limitsJson: string;
}) => {
  const {
    bulk_payments: bulkPaymentsFeature,
    user_initiated_ach_reversals: achReversalsFeature,
  } = features;
  const wireFileImportFeature = Boolean(features.domestic_wire_file_import);
  const passthroughFileImportFeature = canUseAchPassthrough(features);
  const fileImportFeature =
    canViewAchFileImports(features) ||
    wireFileImportFeature ||
    passthroughFileImportFeature;
  // Gates only the ACH continuation screens, kept separate from
  // wireFileImportFeature so neither import type can reach the other's routes.
  const achFileImportUploadFeature = canStartAchFileImport(features);
  const individualPaymentActivityFeature =
    canShowIndividualPaymentActivity(features);
  const canMountPaymentActivityRoutes = canShowPaymentActivity(features);
  const achTemplatesFeature = areAchTemplatesAvailable(features);
  if (!canMountPaymentActivityRoutes) return null;

  const institution = useInstitutionSettings();
  if (!Object.prototype.hasOwnProperty.call(institution, "ach_next_cutoff")) {
    return null;
  }

  const limits = JSON.parse(limitsJson);
  const bulkInitialValues = initialBulkFormValues(institution);
  const scheduledDate = bulkInitialValues.payments[0]?.scheduledDate ?? "";

  return (
    <ComposeProviders components={Providers}>
      <entities.bulkPayments.BulkPaymentForm.Provider
        initialValues={bulkInitialValues}
      >
        <SudoProvider basename={PAYMENT_ACTIVITY_ROUTE}>
          {/* establishSudo routes to /payment-activity/sudo/request-otp, so we need TemplateContextProvider above <Routes> to survive the step-up and not lose the user's row exclusions */}
          <TemplateContextProvider scheduledDate={scheduledDate}>
            <Routes>
              {!individualPaymentActivityFeature && bulkPaymentsFeature && (
                <Route
                  index
                  element={
                    <Navigate to={`/${PAYMENT_ACTIVITY_ROUTE}/bulk`} replace />
                  }
                />
              )}
              {!individualPaymentActivityFeature &&
                !bulkPaymentsFeature &&
                fileImportFeature && (
                  <Route
                    index
                    element={
                      <Navigate
                        to={`/${PAYMENT_ACTIVITY_ROUTE}/file-import`}
                        replace
                      />
                    }
                  />
                )}
              <Route
                element={
                  <PaymentActivityContainer
                    showIndividualPaymentsTab={individualPaymentActivityFeature}
                    showFileImportTab={fileImportFeature}
                  />
                }
              >
                {individualPaymentActivityFeature && (
                  <>
                    <Route index element={<IndividualPaymentContainer />} />
                    <Route
                      path=":activityUuid"
                      element={<IndividualPaymentContainer />}
                    />
                    {/*
                     * two reverse routes, see getReverseRoute / getReverseFromListRoute above for why the URLs
                     * must differ in whether they bind :activityUuid
                     */}
                    {achReversalsFeature && (
                      <>
                        <Route
                          path=":activityUuid/reverse/*"
                          element={<IndividualPaymentContainer />}
                        />
                        <Route
                          path="reverse/:reverseActivityUuid/*"
                          element={<IndividualPaymentContainer />}
                        />
                      </>
                    )}
                  </>
                )}
                {bulkPaymentsFeature && (
                  <>
                    <Route path="bulk" element={<BulkPaymentContainer />} />
                    {achReversalsFeature && (
                      <Route
                        path="bulk/reverse/:reverseBulkPaymentId/*"
                        element={<BulkPaymentContainer />}
                      />
                    )}
                    <Route
                      path="bulk/:bulkPaymentId"
                      element={<BulkPaymentContainer />}
                    />
                    {achReversalsFeature && (
                      <Route
                        path="bulk/:bulkPaymentId/reverse/*"
                        element={<BulkPaymentContainer />}
                      />
                    )}
                  </>
                )}
                {fileImportFeature && (
                  <>
                    <Route
                      path="file-import"
                      element={<FileImportContainer />}
                    />
                    <Route
                      path="file-import/:fileImportId"
                      element={<FileImportContainer />}
                    />
                    <Route
                      path="file-import/ach-passthrough/:fileImportId"
                      element={<FileImportContainer />}
                    />
                    <Route
                      path="file-import/wire/:fileImportId"
                      element={<FileImportContainer />}
                    />
                    {achReversalsFeature && (
                      <Route
                        path="file-import/reverse/:reverseFileImportId/*"
                        element={<FileImportContainer />}
                      />
                    )}
                    {achReversalsFeature && (
                      <Route
                        path="file-import/:fileImportId/reverse/*"
                        element={<FileImportContainer />}
                      />
                    )}
                  </>
                )}
              </Route>
              {bulkPaymentsFeature && achReversalsFeature && (
                <Route
                  path="bulk/:bulkPaymentId/select-reversals/*"
                  element={<BulkSelectReversalScreen />}
                />
              )}
              {fileImportFeature && achReversalsFeature && (
                <Route
                  path="file-import/:fileImportId/select-reversals/*"
                  element={
                    <BulkSelectReversalScreen
                      routeConfig={{
                        paramKey: "fileImportId",
                        getSelectionRoute: getFileImportReversalSelectionRoute,
                        getReverseFromSelectionRoute:
                          getFileImportReverseFromSelectionRoute,
                        backRoute: (id) =>
                          `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${id}`,
                        listRoute: `/${PAYMENT_ACTIVITY_ROUTE}/file-import`,
                      }}
                    />
                  }
                />
              )}
              {passthroughFileImportFeature && (
                <Route
                  path="file-import/:submissionId/payments"
                  element={<NachaPassthroughPaymentsPage />}
                />
              )}
              <Route
                element={
                  <ACHImportContextProvider>
                    <ComposeProviders
                      components={[
                        wireFileImportFeature &&
                          DomesticWireImportContextProvider,
                        passthroughFileImportFeature &&
                          ACHPassthroughImportContextProvider,
                      ].filter(
                        (Provider): Provider is ImportContextProvider =>
                          Provider !== false
                      )}
                    >
                      <Outlet />
                    </ComposeProviders>
                  </ACHImportContextProvider>
                }
              >
                <Route path="upload" element={<Outlet />}>
                    {/* Always mounted so FileUploadScreen can render its empty state. */}
                    <Route index element={<FileUploadScreen />} />
                    {achFileImportUploadFeature && (
                      <>
                        <Route
                          path="map-columns"
                          element={<ColumnMappingScreen />}
                        />
                        <Route
                          path="review-recipients"
                          element={<RecipientReviewScreen />}
                        />
                        <Route
                          path="additional-details"
                          element={<AdditionalDetailsScreen />}
                        />
                      </>
                    )}
                    {wireFileImportFeature && (
                      <Route path="wire" element={<Outlet />}>
                        <Route
                          path="map-columns"
                          element={<WireColumnMappingScreen />}
                        />
                        <Route
                          path="review-recipients"
                          element={<WireRecipientReviewScreen />}
                        />
                        <Route
                          path="payment-details"
                          element={<WirePaymentDetailsScreen />}
                        />
                        <Route
                          path="payments"
                          element={<WirePaymentsTableScreen />}
                        />
                        <Route path="review" element={<WireReviewScreen />} />
                        <Route path="receipt" element={<WireReceiptScreen />} />
                      </Route>
                    )}
                    {passthroughFileImportFeature && (
                      <Route path="passthrough" element={<Outlet />}>
                        <Route
                          path="validation-results"
                          element={<ValidationResultsScreen />}
                        />
                        <Route
                          path="additional-details"
                          element={<PassthroughAdditionalDetailsScreen />}
                        />
                        <Route
                          path="review"
                          element={<PassthroughReviewScreen />}
                        />
                        <Route
                          path="receipt"
                          element={<PassthroughReceiptScreen />}
                        />
                      </Route>
                    )}
                </Route>
                {bulkPaymentsFeature && (
                  <Route path="bulk/new" element={<Outlet />}>
                    <Route
                      index
                      element={
                        achTemplatesFeature ? (
                          <SelectTemplateScreen />
                        ) : (
                          <PaymentDetailsScreen limits={limits} />
                        )
                      }
                    />
                    {achTemplatesFeature && (
                      <Route
                        path="details"
                        element={<PaymentDetailsScreen limits={limits} />}
                      />
                    )}
                    <Route
                      path="payments"
                      element={
                        <RequireBulkTitle>
                          <PaymentsTableScreen limits={limits} />
                        </RequireBulkTitle>
                      }
                    />
                    <Route
                      path="review"
                      element={
                        <RequireBulkTitle>
                          <ReviewBulkScreen />
                        </RequireBulkTitle>
                      }
                    />
                    <Route
                      path="receipt"
                      element={
                        <RequireBulkTitle>
                          <ReceiptBulkScreen />
                        </RequireBulkTitle>
                      }
                    />
                    <Route path="*" element={<BulkNewCatchAll />} />
                  </Route>
                )}
                <Route
                  path={`${SudoRoutes.RequestOTP}`}
                  element={<RequestOTP />}
                />
                <Route
                  path={`${SudoRoutes.SubmitOTP}`}
                  element={<SubmitOTP />}
                />
              </Route>
            </Routes>
          </TemplateContextProvider>
        </SudoProvider>
      </entities.bulkPayments.BulkPaymentForm.Provider>
    </ComposeProviders>
  );
};

const PaymentActivityRoutesWrapper = ({
  features,
  limitsJson,
}: {
  features: Features;
  limitsJson: string;
}) => (
  <InstitutionSettingsContextProvider>
    <PaymentActivityRoutes features={features} limitsJson={limitsJson} />
  </InstitutionSettingsContextProvider>
);

export default PaymentActivityRoutesWrapper;
